import { useEffect, useState } from "react";
import Close from "@mui/icons-material/Close";
import {
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { dayjs, getDataUriFromCsvResponse, parsedAny } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import type {
  AvailableOrganisation,
  AvailableOrganisationArray,
  ExtendedVisionUser,
  FormBuilderField,
} from "@thalamos/vision-common";
import {
  AvailableOrganisationArraySchema,
  dateSchema,
  FieldBuilder,
  isCurrentUserSupervisor,
  OrganisationIdSchema,
  OrganisationRoleSchema,
  orgIdSchema,
  tk,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { PopupDialog } from "../../components/Dialog/PopupDialog.js";
import { OrganisationContext } from "../../components/FormBuilder/OrganisationDropdown/OrganisationContext.js";
import { SinglePageForm } from "../../components/FormBuilder/SinglePageForm/SinglePageForm.js";
import { useUser } from "../../Context/UserContext.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";

const DownloadExtractFormDataSchema = z.object({
  startDate: new FieldBuilder().withSchema(dateSchema).build().schema,
  endDate: new FieldBuilder().withSchema(dateSchema).build().schema,
  orgId: new FieldBuilder().withSchema(orgIdSchema).captureValueLabel().build()
    .schema,
});

export type DownloadExtractFormData = z.infer<
  typeof DownloadExtractFormDataSchema
>;

const DownloadExtractForm = ({
  onSubmit,
  onCancel,
  disableSubmitButton,
  user,
}: {
  onSubmit: (values: DownloadExtractFormData) => void;
  onCancel: () => void;
  disableSubmitButton: boolean;
  user: ExtendedVisionUser | undefined;
}) => {
  const fields: FormBuilderField<
    DownloadExtractFormData,
    "startDate" | "endDate" | "orgId"
  >[] = [];

  const isSupervisor = user ? isCurrentUserSupervisor(user) : false;

  if (isSupervisor) {
    fields.push({
      type: "typography",
      fieldName: "orgId",
      label: tk("admin.incidents.downloadExtractModal.selectOrg"),
      textFn: () => [user?.sessionContext?.organisationName ?? ""],
    });
  } else {
    fields.push({
      type: "organisation",
      fieldName: "orgId",
      label: tk("admin.incidents.downloadExtractModal.selectOrg"),
      filterByRoles: [OrganisationRoleSchema.enum.PoliceForce],
    });
  }

  fields.push(
    {
      type: "date",
      fieldName: "startDate",
      label: tk("admin.incidents.downloadExtractModal.startDate"),
      disableFuture: true,
    },
    {
      type: "date",
      fieldName: "endDate",
      label: tk("admin.incidents.downloadExtractModal.endDate"),
      disableFuture: true,
    },
  );

  const defaultOrgValue = {
    value: isSupervisor ? (user?.sessionContext?.organisationId ?? "") : "",
    valueLabel: isSupervisor
      ? (user?.sessionContext?.organisationName ?? "")
      : "",
  };

  return (
    <SinglePageForm<typeof DownloadExtractFormDataSchema>
      schema={DownloadExtractFormDataSchema}
      initialValues={{
        startDate: { value: "" },
        endDate: { value: "" },
        orgId: defaultOrgValue,
      }}
      content={{ fields }}
      onSubmit={onSubmit}
      canCancel={true}
      onCancel={onCancel}
      confirmButtonLabel={tk("admin.incidents.downloadExtractModal.download")}
      canDisableSubmitButton={true}
      disableSubmitButton={disableSubmitButton}
      ariaLabel="Download Extract"
    />
  );
};

export function DownloadExtractModalInner({
  open,
  close,
  organisations,
  user,
}: {
  open: boolean;
  close: () => void;
  organisations: AvailableOrganisation[];
  user: ExtendedVisionUser | undefined;
}) {
  const { t } = useTranslation();
  const notifications = useToastNotifications();
  const [isDownloading, setIsDownloading] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <OrganisationContext.Provider value={{ organisations }}>
      <PopupDialog
        open={open}
        onClose={close}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        disableDialogRootPadding={true}
      >
        <>
          <DialogTitle sx={{ fontSize: "1.250rem", fontWeight: "bold" }}>
            {t("admin.incidents.downloadExtractModal.title")}
            <IconButton
              aria-label="close"
              onClick={close}
              edge="end"
              sx={{
                position: "absolute",
                right: 15,
                top: 8,
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ marginBottom: "2rem" }}>
              {t("admin.incidents.downloadExtractModal.content")}
            </Typography>
            <DownloadExtractForm
              onSubmit={(values) => {
                const handleSubmit = async (
                  values: DownloadExtractFormData,
                ) => {
                  setIsDownloading(true);
                  try {
                    const response = await api.report.createIncidentReport(
                      OrganisationIdSchema.parse(values.orgId.value),
                      {
                        startDate: values.startDate.value,
                        endDate: values.endDate.value,
                      },
                    );

                    if (response.status === 200) {
                      const dataUri = getDataUriFromCsvResponse(response.data);
                      const todaysDate = dayjs().format("DDMMYYYY");
                      triggerDownload(
                        dataUri,
                        `MHFormsReport${todaysDate}.csv`,
                      );

                      notifications.show(
                        t("admin.incidents.downloadExtractModal.success"),
                        {
                          severity: "success",
                        },
                      );
                    } else {
                      const errorMessage =
                        response.data?.reason ??
                        t("admin.incidents.downloadExtractModal.error");
                      notifications.show(errorMessage, {
                        severity: "error",
                      });
                    }
                  } catch {
                    notifications.show(
                      t("admin.incidents.downloadExtractModal.error"),
                      {
                        severity: "error",
                      },
                    );
                  } finally {
                    setIsDownloading(false);
                  }
                };
                void handleSubmit(values);
              }}
              onCancel={close}
              disableSubmitButton={isDownloading}
              user={user}
            />
          </DialogContent>
        </>
      </PopupDialog>
    </OrganisationContext.Provider>
  );
}

export function SupervisorDownloadExtractModal({
  open,
  close,
  user,
}: {
  open: boolean;
  close: () => void;
  user: ExtendedVisionUser;
}) {
  if (!user.sessionContext) return null;

  const userSessionOrganisationRaw = {
    id: user.sessionContext.organisationId,
    name: user.sessionContext.organisationName,
    role: user.sessionContext.organisationRole,
    managingOrganisationId: user.sessionContext.managingOrganisationId,
  };

  const parsed = AvailableOrganisationArraySchema.safeParse([
    userSessionOrganisationRaw,
  ]);
  const userSessionOrganisation: AvailableOrganisation[] = parsed.success
    ? parsed.data
    : [];

  return (
    <DownloadExtractModalInner
      open={open}
      close={close}
      organisations={userSessionOrganisation}
      user={user}
    />
  );
}

export function DownloadExtractModal({
  open,
  close,
}: {
  open: boolean;
  close: () => void;
}) {
  const { user } = useUser();
  const [{ data, loading }] = apiHooks.organisation.search("", 1000);
  const [organisations, setOrganisations] = useState<AvailableOrganisation[]>(
    [],
  );

  useEffect(() => {
    if (data) {
      const policeForceOrgs = data.results.filter(
        (org) => org.role === OrganisationRoleSchema.enum.PoliceForce,
      );

      const orgs = policeForceOrgs.map((org) => {
        return {
          id: org.id,
          name: org.name,
          role: org.role,
          managingOrganisationId: org.managingOrganisationId,
        };
      });

      const availableOrganisations = parsedAny(
        AvailableOrganisationArraySchema,
        orgs,
      ).unwrapOr([]) satisfies AvailableOrganisationArray;

      setOrganisations(availableOrganisations);
    }
  }, [data]);

  return loading ? (
    <CircularProgress />
  ) : (
    <DownloadExtractModalInner
      open={open}
      close={close}
      organisations={organisations}
      user={user}
    />
  );
}
